// DESK MENU MODEL (headless menu skeleton — README_CURRENT_TASKS §7 follow-up).
//
// One typed, store-derived model of the desk menu SEMANTICS: the entry
// vocabulary, authoritative counts, active/loading state, access gating, and
// the shared entries (parents). It renders nothing. Each surface menu keeps
// composing its OWN DOM/layout/skin and decides which entries to use, which
// to drop, and how to present them — the model only guarantees they all agree
// on the facts (previously each of the six menus re-derived gating and counts
// locally, and the count/visibility semantics of legacy m15 _set_top_menu had
// silently drifted out of most of them).
//
// Ownership: pure selector over SubsStoreState (SSR-safe, no runtime access
// beyond the shared selectors) + a thin useDeskMenu() hook. Labels stay
// surface-owned (forum says "Replies", poo says "Document"); dispatch stays
// with the semantic action modules (deskNavigation etc.) and the leaves.

import { useSubsStore } from '@/state/MsgTreeProvider'
import {
  selectAccessGrant,
  selectAccessState,
  selectDeskIdentity,
  selectMainMsgCounts,
  selectParentsMenu,
} from '@/msgdeskRuntime/state'
import type {
  MsgAccessGrant,
  MsgAccessState,
  ParentsMenuState,
  SubsLoadingState,
  SubsStoreState,
} from '@/msgdeskRuntime/state'
import type { DeskViewMode } from '@/msgdeskRuntime/deskNavigation'

/** The full desk entry vocabulary in canonical order (legacy m15 tab order). */
export const DESK_MENU_MODES: readonly DeskViewMode[] = [
  'msg',
  'answers',
  'submsges',
  'attachments',
  'favs',
  'drafts',
  'trash',
]

export interface DeskMenuTab {
  mode: DeskViewMode
  /** Authoritative count (count_subs_obj with the legacy count_subs fallback). */
  count: number
  /**
   * The [answers, submsges] pair behind `count` for the folder tabs
   * (favs/drafts/trash) — drives the menu section flyout. [0, 0] elsewhere.
   */
  sectionCounts: [number, number]
  isActive: boolean
  /** Top-level tab load in flight (subsLoading elId 0 for this mode). */
  isLoading: boolean
  loadingCount: number
  /**
   * Data fact only: the main tab always represents the desk; every collection
   * tab has items when its authoritative live count is positive. Each surface
   * decides whether an empty collection tab belongs in its own menu.
   */
  hasItems: boolean
  /** Drafts/trash entries require edit access (legacy m15 rule). */
  requiresEdit: boolean
  /**
   * TWO-isMsg-SCOPES (§0.16): whether this entry exists AT ALL on the open
   * desk. Answer desks have no Messages branch, so 'submsges' is unavailable
   * there. Surfaces must not render an unavailable entry (these NavItems
   * carry no legacy mount contract, so dropping them from the DOM is safe).
   */
  available: boolean
  /**
   * Folder tabs only (favs/drafts/trash): whether the entry offers the
   * Answers/Msges section choice. An answer's folders hold only answer-type
   * subs (only messages can log in and star, so even its favs bucket can
   * never receive a message row) — single plain tab there. False elsewhere.
   */
  withSections: boolean
}

export interface DeskMenuModel {
  /** Current render projection mode ('msg', a tab mode, or an editor mode). */
  mode: string
  tabs: DeskMenuTab[]
  tabByMode: Record<DeskViewMode, DeskMenuTab>
  /**
   * Main message kind: true = named message, false = answer (the canonical
   * selectDeskIdentity fact). An answer's folders hold only answer-type subs,
   * so their menu entries and folder pages offer no answers/msges section
   * choice, and the desk has no Messages branch at all.
   */
  mainIsMsg: boolean
  /** The four legacy access booleans (exact selectAccessState output). */
  access: MsgAccessState
  /** The full semantic grant for grant-aware entries. */
  grant: MsgAccessGrant
  /** Shared creation gates (the expression every surface menu was inlining). */
  can: {
    reply: boolean
    createMessage: boolean
    /** FAVS-IDENTITY: any favorites UI (tab, stars, bulk actions) shows. */
    favs: boolean
  }
  /** PARENTS-MENU entry: the ordered chain + visibility (see ParentsMenu.tsx). */
  parents: ParentsMenuState
  subsLoading: SubsLoadingState | null
}

// --- counts ------------------------------------------------------------------
// Ported verbatim from the mailbox menu helpers (the only menu that rendered
// counts) so consuming the model is count-for-count identical.

function countPair(value: unknown): [number, number] {
  if (!Array.isArray(value)) return [0, 0]
  return [Number(value[0] || 0), Number(value[1] || 0)]
}

function sectionPair(main: Record<string, any>, key: 'subs' | 'drafts' | 'trash' | 'favs'): [number, number] {
  // Prefer the LIVE count_subs array (legacy maintains it in place via
  // _subs_count on every toggle/move); count_subs_obj is a boot snapshot that
  // legacy never updates, so reading it first left favs stale after a change
  // (symptom #2) — the tab count kept its boot value until a full reload.
  const legacyIndex = key === 'subs' ? 0 : key === 'drafts' ? 2 : key === 'trash' ? 3 : 4
  const live = main.count_subs?.[legacyIndex]
  if (Array.isArray(live)) return countPair(live)
  return countPair(main.count_subs_obj?.[key])
}

function attachmentsCount(main: Record<string, any>): number {
  const objCount = countPair(main.count_subs_obj?.atts)[0]
  if (objCount) return objCount
  const legacyCount = main.count_subs?.[7]
  if (Array.isArray(legacyCount)) return Number(legacyCount[0] || 0)
  return Number(legacyCount || main.count_atts || main.count_attachments || 0)
}

function tabCount(main: Record<string, any>, mode: DeskViewMode): number {
  switch (mode) {
    case 'msg':
      return 0
    case 'answers':
      return sectionPair(main, 'subs')[0]
    case 'submsges':
      return sectionPair(main, 'subs')[1]
    case 'attachments':
      return attachmentsCount(main)
    case 'favs':
    case 'drafts':
    case 'trash': {
      const pair = sectionPair(main, mode)
      return pair[0] + pair[1]
    }
  }
}

// --- model -------------------------------------------------------------------

export function selectDeskMenuModel(state: SubsStoreState): DeskMenuModel {
  const mode = state.renderProjection.mode
  const access = selectAccessState(state)
  const grant = selectAccessGrant(state)
  const main = selectMainMsgCounts(state)
  // TWO-isMsg-SCOPES (§0.16): the ONE canonical, fail-closed desk identity.
  const { mainIsMsg, canHaveSubmessages } = selectDeskIdentity(state)

  const subsLoading = state.subsLoading
  const topLoadingMode = subsLoading && subsLoading.elId === 0 ? subsLoading.mode : undefined

  const tabs = DESK_MENU_MODES.map((tabMode): DeskMenuTab => {
    const count = tabCount(main ?? {}, tabMode)
    const requiresEdit = tabMode === 'drafts' || tabMode === 'trash'
    const isFolder = tabMode === 'favs' || tabMode === 'drafts' || tabMode === 'trash'
    const sectionCounts: [number, number] = isFolder
      ? sectionPair(main ?? {}, tabMode)
      : [0, 0]
    return {
      mode: tabMode,
      count,
      sectionCounts,
      isActive: mode === tabMode,
      isLoading: topLoadingMode === tabMode,
      loadingCount: subsLoading?.count ?? 0,
      hasItems: tabMode === 'msg' || count > 0,
      requiresEdit,
      // §0.16: answer desks have no Messages branch — the tab is not merely
      // empty, it does not exist there.
      available: tabMode !== 'submsges' || canHaveSubmessages,
      withSections: isFolder && mainIsMsg,
    }
  })

  const tabByMode = Object.fromEntries(tabs.map((tab) => [tab.mode, tab])) as Record<DeskViewMode, DeskMenuTab>

  return {
    mode,
    tabs,
    tabByMode,
    mainIsMsg,
    access,
    grant,
    can: {
      reply: access.showUnlocked && !access.forbidAnswers,
      createMessage: mainIsMsg && access.showUnlocked && !access.forbidSubmsges,
      // FAVS-PRIVACY: the Bookmarks TAB is owner-state only — the current
      // msg's tabs always describe the current msg. A logged visitor still
      // gets the row STARS (selectFavsAllowed) saving into their own bucket,
      // which they review on their OWN desk's tab.
      favs: access.editUnlocked,
    },
    parents: selectParentsMenu(state),
    subsLoading,
  }
}

/** Convenience hook for menu components living under the store provider. */
export function useDeskMenu(): DeskMenuModel {
  const { state } = useSubsStore()
  return selectDeskMenuModel(state)
}
